import React from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { NavGroup } from '../../nav/navTypes';
import { PAGE_CATALOG } from '../../nav/pageCatalog';
import { iconFor } from '../../nav/navIcons';
import { EditorHandlers } from './editorTypes';

interface Props {
  group: NavGroup;
  handlers: EditorHandlers;
}

/**
 * One editable group: rename, delete, draggable page rows with a visibility
 * toggle, and a drop-hint zone. Renders the RAW config (hidden + role-restricted
 * pages included) so an admin can assign any page to any perspective — the
 * sidebar/preview apply the filters, not the editor.
 */
export default function NavGroupCard({ group, handlers }: Props) {
  const labelOf = (id: string) => PAGE_CATALOG[id]?.label ?? id;
  return (
    <Box
      sx={{ border: '1px solid', borderColor: 'grey.400', bgcolor: 'grey.50', p: 1.5, minHeight: 120 }}
      onDragOver={handlers.allowDrop}
      onDrop={() => handlers.dropOnGroup(group.id)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={group.name}
          onChange={(e) => handlers.renameGroup(group.id, e.target.value)}
          variant="standard"
          fullWidth
          inputProps={{ 'aria-label': `Gruppenname ${group.id}` }}
        />
        <IconButton
          size="small"
          aria-label={`Gruppe löschen: ${group.name}`}
          onClick={() => handlers.deleteGroup(group.id)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {group.pages.map((p) => (
        <Box
          key={p.id}
          draggable
          onDragStart={(e) => {
            if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
            handlers.beginDrag(p.id, group.id);
          }}
          onDragOver={handlers.allowDrop}
          onDrop={(e) => {
            e.stopPropagation();
            handlers.dropBeforePage(group.id, p.id);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            my: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            cursor: 'grab',
            opacity: p.hidden ? 0.45 : 1,
          }}
        >
          {iconFor(p.id)}
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {labelOf(p.id)}
          </Typography>
          <IconButton
            size="small"
            aria-label={`${p.hidden ? 'Einblenden' : 'Ausblenden'}: ${labelOf(p.id)}`}
            onClick={() => handlers.toggleVisibility(group.id, p.id)}
          >
            {p.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </Box>
      ))}

      <Box sx={{ mt: 1, p: 1, border: '1px dashed', borderColor: 'grey.400', textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          hierher ziehen
        </Typography>
      </Box>
    </Box>
  );
}
